import {
  AlertCircle,
  Copy,
  Info,
  type LucideIcon,
  Sparkles,
} from "lucide-react";
import { toast } from "sonner";

import { SectionHeader } from "@src/shared/components/general/SectionHeader";
import { Text } from "@src/shared/components/general/Text";
import {
  HorizontalFlexbox,
  VerticalFlexbox,
} from "@src/shared/components/layout/Flexbox";
import { Badge } from "@src/shared/components/ui/badge";
import { Card, CardContent, CardHeader } from "@src/shared/components/ui/card";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@src/shared/components/ui/popover";

/**
 * One piece of coding evidence from the billable-scrubber: a `narrative`
 * (rationale) and the verbatim `quotes` that support it. The popover renders
 * the narrative as a parent with its quotes nested beneath.
 */
export type CodeEvidence = {
  narrative: string;
  quotes: string[];
};

export type MedicalCodeRow = {
  code: string;
  description: string | null;
  creationType: "ai" | "template" | null;
  /**
   * Supporting evidence backing the code, surfaced via the popover. Each item
   * is a narrative plus its supporting quotes (the narrative→quotes hierarchy
   * from the scrubber is preserved).
   */
  evidence?: CodeEvidence[];
  /**
   * CPT billing modifiers from the scrubber's service line (e.g. "25", "59").
   * Up to four slots; non-null ones render as chips next to the code. ICD-10
   * rows leave these null.
   */
  modifier1?: string | null;
  modifier2?: string | null;
  modifier3?: string | null;
  modifier4?: string | null;
  /** Service-line unit count; rendered as "×N" when greater than 1. */
  units?: number | null;
};

/**
 * Coding status from the billable-scrubber, snapshot at the last guidance
 * check: `pending` (scrub still running), `ready` (codes available), `failed`
 * (scrub failed — coding unavailable), `none` (no guidance / not applicable).
 */
export type CodingStatus = "pending" | "ready" | "failed" | "none";

interface CodingCardProps {
  title: string;
  icon: LucideIcon;
  /**
   * When `undefined`, the card renders a static "awaiting suggestions"
   * placeholder (kept for cards that don't yet have a backing endpoint).
   * When defined, the card renders one of: error, loading skeleton,
   * empty state, or the populated list.
   */
  rows?: MedicalCodeRow[];
  isLoading?: boolean;
  isError?: boolean;
  /** Coding status driving the pending (loading) and failed states. */
  codingStatus?: CodingStatus;
}

export const CodingCard = ({
  title,
  icon: Icon,
  rows,
  isLoading = false,
  isError = false,
  codingStatus,
}: CodingCardProps) => {
  return (
    <Card className="min-w-0 flex-1 border-border shadow-soft">
      <CardHeader className="pb-3">
        <HorizontalFlexbox alignItems="center" justifyContent="space-between">
          <HorizontalFlexbox alignItems="center" gap="xs">
            <Icon className="size-4 text-accent" />
            <SectionHeader>{title}</SectionHeader>
          </HorizontalFlexbox>
          <button
            type="button"
            aria-label="Copy text"
            disabled={!rows?.length}
            onClick={() => copyRows(rows)}
            className="rounded p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:cursor-not-allowed disabled:opacity-30"
          >
            <Copy className="size-3.5" />
          </button>
        </HorizontalFlexbox>
      </CardHeader>
      <CardContent className="pt-0">
        <VerticalFlexbox gap="sm">
          {renderBody({ rows, isLoading, isError, codingStatus })}
        </VerticalFlexbox>
      </CardContent>
    </Card>
  );
};

function renderBody({
  rows,
  isLoading,
  isError,
  codingStatus,
}: {
  rows: MedicalCodeRow[] | undefined;
  isLoading: boolean;
  isError: boolean;
  codingStatus: CodingStatus | undefined;
}) {
  if (rows === undefined) {
    return (
      <Text sizing="sm" appearance="subdued" className="italic">
        Awaiting AI suggestions...
      </Text>
    );
  }

  if (isError) {
    return (
      <HorizontalFlexbox
        alignItems="center"
        gap="xs"
        className="text-destructive"
      >
        <AlertCircle className="size-4 shrink-0" />
        <Text sizing="sm" className="italic text-destructive">
          Couldn't load coding suggestions.
        </Text>
      </HorizontalFlexbox>
    );
  }

  // The scrub failed — distinct from a fetch error. Coding won't arrive for
  // this encounter without a re-scrub.
  if (codingStatus === "failed" && rows.length === 0) {
    return (
      <HorizontalFlexbox alignItems="center" gap="xs">
        <AlertCircle className="size-4 shrink-0 text-muted-foreground" />
        <Text sizing="sm" appearance="subdued" className="italic">
          Coding unavailable for this visit.
        </Text>
      </HorizontalFlexbox>
    );
  }

  // Loading: either the request is in flight, or the scrub is still running
  // (codingStatus "pending") and codes haven't landed yet.
  if ((isLoading || codingStatus === "pending") && rows.length === 0) {
    return (
      <>
        {codingStatus === "pending" && (
          <Text sizing="sm" appearance="subdued" className="italic">
            Generating codes…
          </Text>
        )}
        <SkeletonRow />
        <SkeletonRow />
        <SkeletonRow />
      </>
    );
  }

  if (rows.length === 0) {
    return (
      <Text sizing="sm" appearance="subdued" className="italic">
        No codes detected yet.
      </Text>
    );
  }

  return (
    <>
      {rows.map((row) => (
        <Row key={row.code} row={row} />
      ))}
    </>
  );
}

function Row({ row }: { row: MedicalCodeRow }) {
  const modifiers = [
    row.modifier1,
    row.modifier2,
    row.modifier3,
    row.modifier4,
  ].filter((m): m is string => !!m);
  const showUnits = typeof row.units === "number" && row.units > 1;

  return (
    <HorizontalFlexbox
      alignItems="center"
      justifyContent="space-between"
      gap="sm"
      className="py-1"
    >
      <HorizontalFlexbox alignItems="baseline" gap="sm" className="min-w-0">
        <span className="font-mono text-sm">{row.code}</span>
        {modifiers.map((modifier) => (
          <span
            key={`${row.code}-mod-${modifier}`}
            aria-label={`Modifier ${modifier}`}
            className="rounded bg-muted px-1 font-mono text-xs text-muted-foreground"
          >
            {modifier}
          </span>
        ))}
        {showUnits && (
          <span
            aria-label={`${row.units} units`}
            className="font-mono text-xs text-muted-foreground"
          >
            ×{row.units}
          </span>
        )}
        {row.description && (
          <span
            className="min-w-0 truncate text-sm text-muted-foreground"
            title={row.description}
          >
            {row.description}
          </span>
        )}
      </HorizontalFlexbox>
      <HorizontalFlexbox alignItems="center" gap="xs" className="shrink-0">
        {row.evidence && row.evidence.length > 0 && (
          <EvidencePopover code={row.code} evidence={row.evidence} />
        )}
        {row.creationType === "ai" && (
          <Badge
            variant="outline"
            className="gap-1 border-success/30 bg-success/10 text-success"
          >
            <Sparkles className="size-3" />
            AI-suggested
          </Badge>
        )}
      </HorizontalFlexbox>
    </HorizontalFlexbox>
  );
}

function EvidencePopover({
  code,
  evidence,
}: {
  code: string;
  evidence: CodeEvidence[];
}) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={`Evidence for ${code}`}
          className="rounded p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        >
          <Info className="size-3.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80">
        <VerticalFlexbox gap="sm">
          <Text sizing="sm" className="font-medium">
            Evidence for <span className="font-mono">{code}</span>
          </Text>
          {evidence.map((item, i) => (
            <VerticalFlexbox
              // Evidence items have no stable id; index is stable per render.
              key={`${code}-evidence-${i}`}
              gap="xs"
              className="border-border border-l-2 pl-2"
            >
              {item.narrative && (
                <Text sizing="sm" className="font-medium">
                  {item.narrative}
                </Text>
              )}
              {item.quotes.map((quote, j) => (
                <Text
                  key={`${code}-evidence-${i}-quote-${j}`}
                  sizing="sm"
                  appearance="subdued"
                  className="pl-2 italic"
                >
                  “{quote}”
                </Text>
              ))}
            </VerticalFlexbox>
          ))}
        </VerticalFlexbox>
      </PopoverContent>
    </Popover>
  );
}

function SkeletonRow() {
  return (
    <div
      data-testid="coding-skeleton-row"
      className="my-1 flex items-center gap-2"
    >
      <div className="h-4 w-12 animate-pulse rounded bg-muted" />
      <div className="h-4 w-40 animate-pulse rounded bg-muted" />
    </div>
  );
}

async function copyRows(rows: MedicalCodeRow[] | undefined) {
  if (!rows?.length) return;
  const lines = rows.map((r) =>
    r.description ? `${r.code}\t${r.description}` : r.code,
  );
  try {
    await navigator.clipboard.writeText(lines.join("\n"));
    toast.success(`Copied ${rows.length} code${rows.length === 1 ? "" : "s"}`);
  } catch (err) {
    console.error("[CodingCard] clipboard write failed", err);
    toast.error("Couldn't copy — clipboard unavailable");
  }
}
